import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Fade,
  Slide,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  SmartToy as BotIcon,
  School as KnowledgeIcon,
  Chat as ChatIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalConversations: 0,
    todayConversations: 0,
    botUptime: '99.9%',
    responseTime: '1.2s',
    knowledgeBaseSize: 0,
    systemStatus: 'online',
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [conversationData, setConversationData] = useState([]);
  const [userActivityData, setUserActivityData] = useState([]);
  const [botPerformanceData, setBotPerformanceData] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  
  const { user } = useAuth();

  // Default data (will be replaced by API data)
  const defaultConversationData = [
    { name: 'Mo', conversations: 0, resolved: 0 },
    { name: 'Di', conversations: 0, resolved: 0 },
    { name: 'Mi', conversations: 0, resolved: 0 },
    { name: 'Do', conversations: 0, resolved: 0 },
    { name: 'Fr', conversations: 0, resolved: 0 },
    { name: 'Sa', conversations: 0, resolved: 0 },
    { name: 'So', conversations: 0, resolved: 0 },
  ];

  const defaultUserActivityData = Array.from({ length: 24 }, (_, i) => ({
    name: `${i.toString().padStart(2, '0')}:00`,
    users: 0
  }));

  const defaultBotPerformanceData = [
    { name: 'Korrekte Antworten', value: 0, color: '#4CAF50' },
    { name: 'Unklare Antworten', value: 0, color: '#FF9800' },
    { name: 'Fehlerhafte Antworten', value: 0, color: '#F44336' },
  ];

  const defaultRecentActivities = [];

  // Load data function (only called manually)
  const loadData = async () => {
    setLoading(true);
    
    try {
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        setLoading(false);
        return;
      }

      // Load dashboard stats from Admin Backend
      const statsResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/dashboard/stats`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats({
          totalUsers: statsData.totalUsers || 0,
          activeUsers: statsData.activeUsers || 0,
          totalConversations: statsData.totalConversations || 0,
          todayConversations: statsData.todayConversations || 0,
          botUptime: statsData.botUptime || '0%',
          responseTime: statsData.responseTime || '0.0s',
          knowledgeBaseSize: statsData.knowledgeBaseSize || 0,
          systemStatus: statsData.systemStatus || 'offline',
        });
      } else {
        console.error('Failed to load dashboard stats:', statsResponse.status);
      }

      // Load conversation trends from Admin Backend
      const trendsResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/dashboard/conversation-trends?days=7`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (trendsResponse.ok) {
        const trendsData = await trendsResponse.json();
        setConversationData(trendsData);
      }

      // Load user activity from Admin Backend
      const activityResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/dashboard/user-activity`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (activityResponse.ok) {
        const activityData = await activityResponse.json();
        setUserActivityData(activityData);
      }

      // Load bot performance from Admin Backend
      const performanceResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/dashboard/bot-performance`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (performanceResponse.ok) {
        const performanceData = await performanceResponse.json();
        setBotPerformanceData(performanceData);
      }

      // Load recent activities from Admin Backend
      const activitiesResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/dashboard/recent-activities?limit=10`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (activitiesResponse.ok) {
        const activitiesData = await activitiesResponse.json();
        setRecentActivities(activitiesData);
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
    
    setLoading(false);
    setLastUpdated(new Date());
  };

  // Initial load only
  useEffect(() => {
    loadData();
  }, []);

  const handleRefresh = () => {
    loadData();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <CheckCircleIcon />;
    }
  };

  const StatCard = ({ title, value, icon, color, trend, subtitle }) => (
    <Fade in timeout={600}>
      <Card
        sx={{
          height: '100%',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          border: '1px solid #e9ecef',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: color + '20',
                color: color,
              }}
            >
              {icon}
            </Box>
            {trend && (
              <Chip
                label={trend}
                size="small"
                color="success"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
          
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#000000' }}>
            {value}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {title}
          </Typography>
          
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Fade>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Willkommen zurück, {user?.username || user?.name || 'Admin'}! 👋
            </Typography>
            <Tooltip
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#FFD700' }}>
                    Dashboard-Übersicht
                  </Typography>
                  <Typography variant="body2" sx={{ maxWidth: 300, lineHeight: 1.4 }}>
                    Hier sehen Sie alle wichtigen Kennzahlen und Statistiken Ihres KiChatbot Systems. 
                    Die Daten werden automatisch aktualisiert und zeigen Ihnen die Performance und Nutzung.
                  </Typography>
                </Box>
              }
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 350,
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    '& .MuiTooltip-arrow': {
                      color: 'rgba(0, 0, 0, 0.9)',
                    },
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  p: 0.5,
                  color: '#FFD700',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    color: '#F57F17',
                  },
                }}
              >
                <InfoIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Hier ist eine Übersicht über Ihr KiChatbot System
          </Typography>
        </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Letzte Aktualisierung: {lastUpdated.toLocaleTimeString('de-DE')}
            </Typography>
            <Tooltip title="Aktualisieren">
              <IconButton
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  backgroundColor: '#FFD700',
                  color: '#000000',
                  '&:hover': {
                    backgroundColor: '#F57F17',
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {loading && <LinearProgress sx={{ mb: 2 }} />}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Gesamte Benutzer"
            value={stats.totalUsers.toLocaleString()}
            icon={<PeopleIcon sx={{ fontSize: 28 }} />}
            color="#2196F3"
            trend={stats.trends?.users || "+0%"}
            subtitle="Diese Woche"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Aktive Benutzer"
            value={stats.activeUsers}
            icon={<TrendingUpIcon sx={{ fontSize: 28 }} />}
            color="#4CAF50"
            trend={stats.trends?.active || "+0%"}
            subtitle="Heute online"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Gespräche heute"
            value={stats.todayConversations}
            icon={<ChatIcon sx={{ fontSize: 28 }} />}
            color="#FF9800"
            trend={stats.trends?.conversations || "+0%"}
            subtitle="vs. gestern"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Bot Verfügbarkeit"
            value={stats.botUptime}
            icon={<BotIcon sx={{ fontSize: 28 }} />}
            color="#9C27B0"
            subtitle="Letzte 30 Tage"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Conversation Trends */}
        <Grid item xs={12} md={8}>
          <Slide in timeout={800}>
            <Card sx={{ height: 400, borderRadius: 3 }}>
              <CardContent sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Gespräche pro Tag
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={conversationData.length > 0 ? conversationData : defaultConversationData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" stroke="#666" />
                    <YAxis stroke="#666" />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="conversations"
                      stackId="1"
                      stroke="#FFD700"
                      fill="url(#colorConversations)"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="resolved"
                      stackId="1"
                      stroke="#4CAF50"
                      fill="url(#colorResolved)"
                      strokeWidth={2}
                    />
                    <defs>
                      <linearGradient id="colorConversations" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FFD700" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#FFD700" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#4CAF50" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* Bot Performance */}
        <Grid item xs={12} md={4}>
          <Slide in timeout={1000}>
            <Card sx={{ height: 500, borderRadius: 3 }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Bot Performance
                </Typography>
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={botPerformanceData.length > 0 ? botPerformanceData : defaultBotPerformanceData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {(botPerformanceData.length > 0 ? botPerformanceData : defaultBotPerformanceData).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ mt: 2, flexGrow: 1 }}>
                    {(botPerformanceData.length > 0 ? botPerformanceData : defaultBotPerformanceData).map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: item.color,
                            borderRadius: '50%',
                            mr: 1,
                          }}
                        />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.value}%
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      </Grid>

      {/* System Status and Recent Activity */}
      <Grid container spacing={3}>
        {/* System Status */}
        <Grid item xs={12} md={6}>
          <Slide in timeout={1200}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  System Status
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1">Bot Status</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getStatusIcon(stats.systemStatus)}
                      <Chip
                        label={stats.systemStatus.toUpperCase()}
                        color={getStatusColor(stats.systemStatus)}
                        size="small"
                        sx={{ ml: 1, fontWeight: 600 }}
                      />
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1">Antwortzeit</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                      {stats.responseTime}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1">Wissensdatenbank</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {stats.knowledgeBaseSize.toLocaleString()} Dokumente
                    </Typography>
                  </Box>
                </Box>
                
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<SpeedIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #FFD700 0%, #F57F17 100%)',
                    color: '#000000',
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                  }}
                >
                  System Details anzeigen
                </Button>
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Slide in timeout={1400}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Letzte Aktivitäten
                </Typography>
                
                <Box>
                  {(recentActivities.length > 0 ? recentActivities : defaultRecentActivities).map((activity, index) => (
                    <Box
                      key={activity.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                      }}
                    >
                      <Box
                        sx={{
                          mr: 2,
                          mt: 0.5,
                        }}
                      >
                        {getStatusIcon(activity.type)}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {activity.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {activity.time}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
                
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: '#FFD700',
                    color: '#FFD700',
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#F57F17',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    },
                  }}
                >
                  Alle Aktivitäten anzeigen
                </Button>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      </Grid>

      {/* Additional Metrics Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Response Time & Accuracy */}
        <Grid item xs={12} md={6}>
          <Slide in timeout={1400}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Performance Metriken
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Durchschnittliche Antwortzeit
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                      {stats.responseTime}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={85} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#4CAF50',
                      },
                    }} 
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Antwortgenauigkeit
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2196F3' }}>
                      94.2%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={94.2} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#2196F3',
                      },
                    }} 
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Kundenzufriedenheit
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#FF9800' }}>
                      4.8/5
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={96} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#FF9800',
                      },
                    }} 
                  />
                </Box>
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* Knowledge Base & System Info */}
        <Grid item xs={12} md={6}>
          <Slide in timeout={1600}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  System Informationen
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <KnowledgeIcon sx={{ mr: 2, color: '#9C27B0' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Wissensdatenbank
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {stats.knowledgeBaseSize} Dokumente
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SpeedIcon sx={{ mr: 2, color: '#4CAF50' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        System Auslastung
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        23% CPU
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <StorageIcon sx={{ mr: 2, color: '#FF9800' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Speicherverbrauch
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        2.4 GB / 8 GB
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Letzte Wartung:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Vor 2 Tagen
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Slide in timeout={1800}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Schnellaktionen
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<BotIcon />}
                      sx={{
                        borderColor: '#FFD700',
                        color: '#FFD700',
                        fontWeight: 600,
                        py: 1.5,
                        '&:hover': {
                          borderColor: '#F57F17',
                          backgroundColor: 'rgba(255, 215, 0, 0.1)',
                        },
                      }}
                    >
                      Bot testen
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<KnowledgeIcon />}
                      sx={{
                        borderColor: '#FFD700',
                        color: '#FFD700',
                        fontWeight: 600,
                        py: 1.5,
                        '&:hover': {
                          borderColor: '#F57F17',
                          backgroundColor: 'rgba(255, 215, 0, 0.1)',
                        },
                      }}
                    >
                      Wissen hinzufügen
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<PeopleIcon />}
                      sx={{
                        borderColor: '#FFD700',
                        color: '#FFD700',
                        fontWeight: 600,
                        py: 1.5,
                        '&:hover': {
                          borderColor: '#F57F17',
                          backgroundColor: 'rgba(255, 215, 0, 0.1)',
                        },
                      }}
                    >
                      Benutzer verwalten
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<ChatIcon />}
                      sx={{
                        borderColor: '#FFD700',
                        color: '#FFD700',
                        fontWeight: 600,
                        py: 1.5,
                        '&:hover': {
                          borderColor: '#F57F17',
                          backgroundColor: 'rgba(255, 215, 0, 0.1)',
                        },
                      }}
                    >
                      Chat-Sessions
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
